import { useMemo, useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isToday,
  parseISO,
  addMonths,
  subMonths,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../components/ConfirmDialog';
import { ChevronLeft, ChevronRight, Lock, X, Loader2, Trash2 } from 'lucide-react';
import {
  useBookings,
  useBlockedDates,
  useCreateBlocked,
  useDeleteBlocked,
  useActiveProperty,
  type Booking,
  type BlockedDate,
} from '../../../hooks/useManagement';
import PropertySelector from '../../../components/PropertySelector';

type DayStatus =
  | { kind: 'confirmed'; guest: string }
  | { kind: 'pending'; guest: string }
  | { kind: 'blocked'; reason: string }
  | { kind: 'available' };

// A date is "covered" by a booking when it falls within [check_in, check_out).
function coversBooking(day: Date, b: Booking): boolean {
  const start = parseISO(b.check_in);
  const end = parseISO(b.check_out);
  return day >= startOfDayLocal(start) && day < startOfDayLocal(end);
}

// end_date is EXCLUSIVE (the checkout / first free day), matching the booking
// engine. A day is blocked when start <= day < end.
function inBlockedRange(day: Date, bd: BlockedDate): boolean {
  const start = startOfDayLocal(parseISO(bd.start_date));
  const end = startOfDayLocal(parseISO(bd.end_date));
  if (end <= start) return false;
  return day >= start && day < end;
}

function startOfDayLocal(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export default function ManageCalendarPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { propertyId, properties } = useActiveProperty(true);
  const hasProperties = properties.length > 0;
  const { data: bookings, isLoading: bookingsLoading } = useBookings('all', propertyId);
  const { data: blocked, isLoading: blockedLoading } = useBlockedDates(propertyId);
  const createBlocked = useCreateBlocked();
  const deleteBlocked = useDeleteBlocked();

  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [form, setForm] = useState({ start: '', end: '', reason: '' });

  const isLoading = bookingsLoading || blockedLoading;

  // Monday-first weekday abbreviations, localised.
  const WEEKDAYS = [
    t('portal.manage.calendar.weekday.mon', 'Lu'),
    t('portal.manage.calendar.weekday.tue', 'Ma'),
    t('portal.manage.calendar.weekday.wed', 'Mi'),
    t('portal.manage.calendar.weekday.thu', 'Ju'),
    t('portal.manage.calendar.weekday.fri', 'Vi'),
    t('portal.manage.calendar.weekday.sat', 'Sá'),
    t('portal.manage.calendar.weekday.sun', 'Do'),
  ];

  // Only confirmed + pending bookings affect availability.
  const relevantBookings = useMemo(
    () => (bookings ?? []).filter((b) => b.status === 'confirmed' || b.status === 'pending'),
    [bookings],
  );
  const blockedRanges = useMemo(() => blocked ?? [], [blocked]);

  function getDayStatus(day: Date): DayStatus {
    const booking = relevantBookings.find((b) => coversBooking(day, b));
    if (booking) {
      return booking.status === 'confirmed'
        ? { kind: 'confirmed', guest: booking.guest_name }
        : { kind: 'pending', guest: booking.guest_name };
    }
    const bd = blockedRanges.find((r) => inBlockedRange(day, r));
    if (bd) return { kind: 'blocked', reason: bd.reason ?? t('portal.manage.calendar.blockedDefault') };
    return { kind: 'available' };
  }

  const handleCreate = () => {
    if (!form.start || !form.end) return;
    createBlocked.mutate(
      {
        start_date: form.start,
        end_date: form.end,
        reason: form.reason || undefined,
        property_id: propertyId ?? undefined,
      },
      {
        onSuccess: () => {
          setForm({ start: '', end: '', reason: '' });
          setShowBlockModal(false);
        },
      },
    );
  };

  const sortedBlocked = useMemo(
    () => [...blockedRanges].sort((a, b) => a.start_date.localeCompare(b.start_date)),
    [blockedRanges],
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('portal.manage.calendar.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('portal.manage.calendar.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowBlockModal(true)}
          disabled={!hasProperties}
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors shrink-0"
        >
          <Lock className="w-4 h-4" /> {t('portal.manage.calendar.blockDates')}
        </button>
      </div>

      {/* Property selector */}
      <PropertySelector />

      {!hasProperties && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center text-slate-500">
          {t('portal.manage.selector.noPropertyHint', 'Primero crea un alojamiento para gestionar su calendario.')}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs">
        {[
          { color: 'bg-emerald-500', label: t('portal.manage.calendar.legendConfirmed') },
          { color: 'bg-amber-400', label: t('portal.manage.calendar.legendPending') },
          { color: 'bg-slate-400', label: t('portal.manage.calendar.legendBlocked') },
          { color: 'bg-white border border-slate-200', label: t('portal.manage.calendar.legendAvailable') },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${color}`} />
            <span className="text-slate-600">{label}</span>
          </div>
        ))}
      </div>

      {!hasProperties ? null : isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-teal-500" size={32} />
        </div>
      ) : (
        <>
          {/* Two months side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[currentMonth, addMonths(currentMonth, 1)].map((month, mi) => {
              const mStart = startOfMonth(month);
              const mEnd = endOfMonth(month);
              const mDays = eachDayOfInterval({ start: mStart, end: mEnd });
              const mStartDow = (mStart.getDay() + 6) % 7; // 0 = Monday
              const pad = Array.from({ length: mStartDow });

              return (
                <div key={mi} className="bg-white rounded-xl border border-slate-200 p-5">
                  {/* Month header */}
                  <div className="flex items-center justify-between mb-4">
                    {mi === 0 ? (
                      <button
                        onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
                        className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                        aria-label={t('portal.manage.calendar.prevMonth')}
                      >
                        <ChevronLeft className="w-4 h-4 text-slate-600" />
                      </button>
                    ) : (
                      <div className="w-7" />
                    )}
                    <h3 className="font-semibold text-slate-900 capitalize">
                      {format(month, 'MMMM yyyy', { locale: es })}
                    </h3>
                    {mi === 1 ? (
                      <button
                        onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
                        className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                        aria-label={t('portal.manage.calendar.nextMonth')}
                      >
                        <ChevronRight className="w-4 h-4 text-slate-600" />
                      </button>
                    ) : (
                      <div className="w-7" />
                    )}
                  </div>

                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 mb-2">
                    {WEEKDAYS.map((d) => (
                      <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">
                        {d}
                      </div>
                    ))}
                  </div>

                  {/* Days grid */}
                  <div className="grid grid-cols-7 gap-0.5">
                    {pad.map((_, i) => (
                      <div key={`pad-${i}`} />
                    ))}
                    {mDays.map((day) => {
                      const status = getDayStatus(day);
                      const today = isToday(day);
                      const title =
                        status.kind === 'confirmed' || status.kind === 'pending'
                          ? status.guest
                          : status.kind === 'blocked'
                            ? status.reason
                            : t('portal.manage.calendar.available');

                      return (
                        <div
                          key={day.toISOString()}
                          title={title}
                          className={[
                            'relative h-9 flex items-center justify-center text-xs rounded cursor-default transition-colors',
                            today ? 'ring-2 ring-teal-500 ring-inset' : '',
                            status.kind === 'confirmed' ? 'bg-emerald-500 text-white font-medium' : '',
                            status.kind === 'pending' ? 'bg-amber-400 text-amber-900 font-medium' : '',
                            status.kind === 'blocked' ? 'bg-slate-400 text-white' : '',
                            status.kind === 'available' ? 'text-slate-700 hover:bg-slate-100' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {format(day, 'd')}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Blocked dates list */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">{t('portal.manage.calendar.blockedList')}</h3>
            {sortedBlocked.length === 0 ? (
              <p className="text-sm text-slate-400">{t('portal.manage.calendar.noBlocked')}</p>
            ) : (
              <div className="space-y-2">
                {sortedBlocked.map((bd) => (
                  <div
                    key={bd.id}
                    className="flex items-center justify-between gap-3 py-2 px-3 bg-slate-50 rounded-lg text-sm"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Lock className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-slate-700 font-medium">
                          {format(parseISO(bd.start_date), 'd MMM', { locale: es })} –{' '}
                          {format(parseISO(bd.end_date), 'd MMM yyyy', { locale: es })}
                        </span>
                        {bd.reason && <span className="text-slate-400 ml-2">· {bd.reason}</span>}
                      </div>
                    </div>
                    <button
                      onClick={async () => { if ((await confirm({ title: t('portal.manage.calendar.deleteBlockedTitle', 'Eliminar bloqueo'), message: t('portal.manage.calendar.deleteBlockedConfirm', '¿Eliminar este bloqueo de fechas?'), confirmLabel: t('common.deleteYes', 'Sí, eliminar'), tone: 'danger' })) === 'confirm') deleteBlocked.mutate(bd.id); }}
                      disabled={deleteBlocked.isPending}
                      className="p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 rounded transition-colors disabled:opacity-50 shrink-0"
                      aria-label={t('portal.manage.calendar.deleteBlock')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Block modal */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-slate-900 text-lg">{t('portal.manage.calendar.modalTitle')}</h3>
              <button
                onClick={() => setShowBlockModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label={t('portal.manage.calendar.close')}
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('portal.manage.calendar.from')}</label>
                  <input
                    type="date"
                    value={form.start}
                    onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('portal.manage.calendar.to')}</label>
                  <input
                    type="date"
                    value={form.end}
                    min={form.start || undefined}
                    onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <p className="-mt-2 text-xs text-slate-500">
                {t('portal.manage.calendar.endExclusiveHint', 'La fecha «Hasta» es el día de salida (la primera noche libre): ese día no se bloquea.')}
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('portal.manage.calendar.reason')}</label>
                <input
                  type="text"
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder={t('portal.manage.calendar.reasonPlaceholder')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              {createBlocked.isError && (
                <p className="text-sm text-rose-600">{t('portal.manage.calendar.error')}</p>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowBlockModal(false)}
                  className="flex-1 border border-slate-300 text-slate-700 text-sm font-medium py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {t('portal.manage.calendar.cancel')}
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!form.start || !form.end || createBlocked.isPending}
                  className="flex-1 flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {createBlocked.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('portal.manage.calendar.block')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
